import { useState, useEffect, useMemo, useCallback } from "react";
import { sGet } from "../../utils/staffApi";
import {
  C,
  Stat,
  BarChart,
  LoadingSpinner,
  Badge,
  Table,
  STATUS_COLORS,
  Btn,
  EmptyState,
  SearchBar,
} from "./AdminPanel";
import "../../styles/dashboard.css";

import {
  FiRefreshCw,
  FiDownload,
  FiBox,
  FiTrendingUp,
  FiTag,
  FiUsers,
  FiClock,
  FiArrowRight,
  FiAlertTriangle,
  FiChevronRight,
  FiPackage,
  FiPercent,
  FiDollarSign,
  FiShoppingCart,
  FiTruck,
  FiCheckCircle,
  FiXCircle,
  FiEye,
  FiMoreVertical,
} from "react-icons/fi";
import {
  FaRupeeSign,
  FaTrophy,
  FaChartLine,
  FaStore,
  FaRegCalendarAlt,
} from "react-icons/fa";
import {
  MdMonetizationOn,
  MdShoppingCart,
  MdTrendingUp,
  MdTrendingDown,
} from "react-icons/md";
import { useNavigate } from "react-router-dom";

/* ─── Helper Functions ─────────────────────────────────────── */
const fmt = (n) => (n ?? 0).toLocaleString("en-IN");
const fmtCompact = (n) => {
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(1)}Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(1)}L`;
  if (n >= 1000) return `₹${(n / 1000).toFixed(1)}K`;
  return `₹${n}`;
};
const fmtPct = (n, t) => (t > 0 ? Math.round((n / t) * 100) : 0);

/* ─── Enhanced Trend Badge ──────────────────────────────────── */
function Trend({ value, label, size = "sm" }) {
  const up = value >= 0;
  return (
    <div className={`trend-enhanced ${up ? "up" : "down"} ${size}`}>
      <span className="trend-icon">
        {up ? <MdTrendingUp /> : <MdTrendingDown />}
      </span>
      <span className="trend-value">{Math.abs(value)}%</span>
      <span className="trend-label">{label}</span>
    </div>
  );
}

/* ─── Revenue Sparkline ────────────────────────────────────── */
function RevenueSparkline({ data }) {
  if (!data?.length) return null;
  const max = Math.max(...data.map((d) => d.revenue || 0));
  return (
    <div className="sparkline-enhanced">
      {data.slice(-12).map((d, i) => (
        <div
          key={i}
          className="sparkline-bar"
          style={{
            height: `${(d.revenue / max) * 32}px`,
            background: `linear-gradient(180deg, ${C.primary}, ${C.primaryDark})`,
          }}
        />
      ))}
    </div>
  );
}

/* ─── Order Status Donut Preview ───────────────────────────── */
function StatusDonutPreview({ data }) {
  if (!data?.length) return null;
  const total = data.reduce((s, x) => s + x.count, 0);
  let cumulative = 0;
  return (
    <div className="donut-preview-enhanced">
      <div className="donut-rings">
        {data.map((s, i) => {
          const percentage = (s.count / total) * 100;
          const start = cumulative;
          cumulative += percentage;
          const angle = (percentage / 100) * 360;
          return (
            <div
              key={s._id}
              className="donut-segment"
              style={{
                transform: `rotate(${start * 3.6}deg)`,
                background: `conic-gradient(${STATUS_COLORS[s._id]?.text || C.primary} 0deg ${angle}deg, transparent ${angle}deg)`,
              }}
            />
          );
        })}
      </div>
      <div className="donut-center">
        <span className="donut-total">{total}</span>
        <span className="donut-label">Orders</span>
      </div>
    </div>
  );
}

/* ─── Section Header Enhanced ──────────────────────────────── */
function SectionHeader({ icon: Icon, color, title, sub, action, badge }) {
  return (
    <div className="section-header-enhanced">
      <div className="section-header-left">
        <div className="section-icon" style={{ color }}>
          <Icon />
        </div>
        <div>
          <div className="section-title">{title}</div>
          <div className="section-sub">{sub}</div>
        </div>
        {badge && <span className="section-badge">{badge}</span>}
      </div>
      {action && <div className="section-action">{action}</div>}
    </div>
  );
}

/* ─── KPI Card Enhanced ────────────────────────────────────── */
function KPICard({
  title,
  value,
  change,
  icon: Icon,
  color,
  subtext,
  trendData,
}) {
  return (
    <div className="kpi-card-enhanced">
      <div className="kpi-card-header">
        <div className="kpi-icon" style={{ background: `${color}15`, color }}>
          <Icon />
        </div>
        {change !== undefined && (
          <div
            className={`kpi-change ${change >= 0 ? "positive" : "negative"}`}
          >
            {change >= 0 ? "↑" : "↓"} {Math.abs(change)}%
          </div>
        )}
      </div>
      <div className="kpi-value">{value}</div>
      <div className="kpi-title">{title}</div>
      {subtext && <div className="kpi-subtext">{subtext}</div>}
      {trendData && <RevenueSparkline data={trendData} />}
    </div>
  );
}

/* ─── Recent Order Row ─────────────────────────────────────── */
function RecentOrderRow({ order, onClick }) {
  const statusColor = STATUS_COLORS[order.orderStatus]?.text || C.muted;
  return (
    <div
      className="recent-order-row-enhanced"
      onClick={() => onClick(order.orderId)}
    >
      <div className="order-info">
        <div className="order-id">#{order.orderId}</div>
        <div className="order-customer">
          <div className="customer-avatar">
            {(order.user?.name || "G").charAt(0).toUpperCase()}
          </div>
          <span>{order.user?.name || "Guest"}</span>
        </div>
      </div>
      <div className="order-details">
        <div className="order-amount">₹{fmt(order.totalAmount)}</div>
        <div className="order-status" style={{ color: statusColor }}>
          <span className="status-dot" style={{ background: statusColor }} />
          {order.orderStatus}
        </div>
        <div className="order-date">
          {new Date(order.createdAt).toLocaleDateString("en-IN", {
            day: "numeric",
            month: "short",
          })}
        </div>
      </div>
    </div>
  );
}

/* ─── Top Product Card ─────────────────────────────────────── */
function TopProductCard({ product, rank, maxRevenue }) {
  const revenuePercent = (product.revenue / maxRevenue) * 100;
  const rankColors = ["#fbbf24", "#e5e7eb", "#fcd34d"];
  return (
    <div className="top-product-enhanced">
      <div
        className="product-rank"
        style={{ background: rankColors[rank] || "#e2e8f0" }}
      >
        #{rank + 1}
      </div>
      <div className="product-info">
        <div className="product-name">{product.name}</div>
        <div className="product-stats">
          <span className="product-sales">
            <FiPackage size={12} /> {fmt(product.sales)} sold
          </span>
          <span className="product-revenue">₹{fmt(product.revenue)}</span>
        </div>
        <div className="product-revenue-bar">
          <div
            className="revenue-fill"
            style={{ width: `${revenuePercent}%` }}
          />
        </div>
      </div>
    </div>
  );
}

export default function Dashboard({ toast }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [timeRange, setTimeRange] = useState("month");
  const navigate = useNavigate()

  const fetchData = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true);
      else setRefreshing(true);
      try {
        const result = await sGet("/dashboard");
        setData(result);
        setLastUpdated(new Date());
      } catch (e) {
        toast?.(e.message, "error");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [toast],
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const id = setInterval(() => fetchData(true), 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [fetchData]);

  const today = useMemo(
    () =>
      new Intl.DateTimeFormat("en-IN", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      }).format(new Date()),
    [],
  );

  const lastUpdatedStr = useMemo(() => {
    if (!lastUpdated) return null;
    return new Intl.DateTimeFormat("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
    }).format(lastUpdated);
  }, [lastUpdated]);

  const monthlySales = data?.monthlySales ?? [];
  const revenueChange = useMemo(() => {
    if (monthlySales.length < 2) return 0;
    const last = monthlySales[monthlySales.length - 1]?.revenue ?? 0;
    const prev = monthlySales[monthlySales.length - 2]?.revenue ?? 0;
    if (prev === 0) return 0;
    return Math.round(((last - prev) / prev) * 100);
  }, [monthlySales]);

  const ordersChange = useMemo(() => {
    if (monthlySales.length < 2) return 0;
    const last = monthlySales[monthlySales.length - 1]?.orders ?? 0;
    const prev = monthlySales[monthlySales.length - 2]?.orders ?? 0;
    if (prev === 0) return 0;
    return Math.round(((last - prev) / prev) * 100);
  }, [monthlySales]);

  const handleExportAll = useCallback(() => {
    if (!data) return;
    const rows = [];
    rows.push(`"Dashboard Export — ${new Date().toLocaleString("en-IN")}"`);
    rows.push("");
    rows.push('"=== SUMMARY ==="');
    rows.push(`"Total Orders","${data.stats?.totalOrders ?? 0}"`);
    rows.push(`"Total Revenue","₹${fmt(data.stats?.totalRevenue)}"`);
    rows.push(`"Total Products","${data.stats?.totalProducts ?? 0}"`);
    rows.push(`"Total Customers","${data.stats?.totalUsers ?? 0}"`);

    const blob = new Blob([rows.join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `dashboard_${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
    toast?.("Dashboard exported successfully", "success");
  }, [data, toast]);

  if (loading) {
    return (
      <div className="dashboard-enhanced">
        <div className="dashboard-loading">
          <LoadingSpinner size="lg" text="Loading dashboard..." />
        </div>
      </div>
    );
  }

  const {
    stats = {},
    recentOrders = [],
    ordersByStatus = [],
    monthlySales: finalMonthlySales = [],
    topProducts = [],
    lowStock = [],
  } = data ?? {};

  const totalStatusOrders = ordersByStatus.reduce((s, x) => s + x.count, 0);
  const maxRevenue = Math.max(
    ...finalMonthlySales.map((m) => m.revenue ?? 0),
    1,
  );
  const maxProductRevenue = Math.max(
    ...topProducts.map((p) => p.revenue ?? 0),
    1,
  );

  return (
    <div className="dashboard-enhanced">
      {/* Header Section */}
      <div className="dashboard-header-enhanced">
        <div>
          {/* <h1 className="dashboard-title">Dashboard</h1> */}
          <div className="dashboard-date-info">
            <FaRegCalendarAlt size={12} />
            <span>{today}</span>
            {lastUpdatedStr && (
              <>
                <span className="dot">•</span>
                <span>Updated {lastUpdatedStr}</span>
              </>
            )}
          </div>
        </div>
        <div className="dashboard-actions">

          <Btn
            variant="ghost"
            size="sm"
            onClick={() => fetchData(true)}
            disabled={refreshing}
            icon={<FiRefreshCw className={refreshing ? "spin" : ""} />}
          >
            {refreshing ? "Refreshing..." : "Refresh"}
          </Btn>
          <Btn
            variant="primary"
            size="sm"
            onClick={handleExportAll}
            icon={<FiDownload />}
          >
            Export
          </Btn>
        </div>
      </div>

      {/* KPI Grid */}
      <div className="kpi-grid-enhanced">
        <KPICard
          title="Total Orders"
          value={fmt(stats?.totalOrders)}
          change={ordersChange}
          icon={FiShoppingCart}
          color={C.primary}
          subtext={`${fmt(stats?.totalOrders || 0)} orders all time`}
          trendData={finalMonthlySales}
        />
        <KPICard
          title="Total Revenue"
          value={fmtCompact(stats?.totalRevenue)}
          change={revenueChange}
          icon={FaRupeeSign}
          color={C.green}
          subtext={`₹${fmt(stats?.totalRevenue || 0)} total`}
          trendData={finalMonthlySales}
        />
        <KPICard
          title="Active Products"
          value={fmt(stats?.totalProducts)}
          icon={FiTag}
          color={C.warning}
          subtext={
            lowStock.length > 0 ? `${lowStock.length} low stock` : "All stocked"
          }
        />
        <KPICard
          title="Total Customers"
          value={fmt(stats?.totalUsers)}
          icon={FiUsers}
          color={C.info}
          subtext="Registered accounts"
        />
      </div>

      {/* Operations Section */}
      <div className="operations-section-enhanced">
        {/* Recent Orders */}
        <div className="recent-orders-card">
          <SectionHeader
            icon={FiClock}
            color={C.info}
            title="Recent Orders"
            sub="Latest transactions"
            action={
              <Btn
                variant="ghost"
                size="sm"
                onClick={() => navigate("/admin/orders")}
              >
                View All <FiArrowRight />
              </Btn>
            }
          />
          <div className="recent-orders-list">
            {recentOrders.length === 0 ? (
              <EmptyState
                icon={FiBox}
                title="No orders"
                description="No recent orders found"
              />
            ) : (
              recentOrders
                .slice(0, 6)
                .map((order) => (
                  <RecentOrderRow
                    key={order.orderId}
                    order={order}
                    onClick={(id) => navigate?.(`/orders/${id}`)}
                  />
                ))
            )}
          </div>
        </div>

        {/* Right Sidebar */}
        <div className="operations-sidebar">
          {/* Order Status Breakdown */}
          <div className="status-card">
            <SectionHeader
              icon={FiTrendingUp}
              color={C.green}
              title="Order Fulfillment"
              sub="Status distribution"
            />
            {ordersByStatus.length === 0 ? (
              <EmptyState
                icon={FiPercent}
                title="No data"
                description="No status data available"
              />
            ) : (
              <div className="status-breakdown-enhanced">
                <StatusDonutPreview data={ordersByStatus} />
                <div className="status-list">
                  {ordersByStatus.map((s) => {
                    const colors = STATUS_COLORS[s._id] ?? { text: C.muted };
                    const pct = fmtPct(s.count, totalStatusOrders);
                    return (
                      <div key={s._id} className="status-item">
                        <div className="status-label">
                          <span
                            className="status-dot"
                            style={{ background: colors.text }}
                          />
                          <span>{s._id}</span>
                        </div>
                        <div className="status-stats">
                          <span className="status-count">{fmt(s.count)}</span>
                          <span className="status-pct">{pct}%</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Low Stock Alert */}
          {lowStock.length > 0 && (
            <div className="alert-card">
              <SectionHeader
                icon={FiAlertTriangle}
                color={C.red}
                title="Low Stock Alert"
                sub={`${lowStock.length} product${lowStock.length !== 1 ? "s" : ""} need attention`}
                action={
                  <Btn
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate("/admin/products?filter=low-stock")}
                  >
                    Manage <FiChevronRight />
                  </Btn>
                }
              />
              <div className="low-stock-list">
                {lowStock.slice(0, 4).map((p) => (
                  <div key={p._id} className="low-stock-item">
                    <div className="product-info">
                      <span className="product-name">{p.name}</span>
                      <span className="product-stock">{p.stock} left</span>
                    </div>
                    <div className="stock-bar">
                      <div
                        className="stock-fill"
                        style={{
                          width: `${Math.min((p.stock / 20) * 100, 100)}%`,
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Top Products Preview */}
          {topProducts.length > 0 && (
            <div className="top-products-card">
              <SectionHeader
                icon={FaTrophy}
                color={C.gold}
                title="Top Performers"
                sub="Best selling products"
                action={
                  <Btn
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate("/admin/products")}
                  >
                    View All <FiChevronRight />
                  </Btn>
                }
              />
              <div className="top-products-list">
                {topProducts.slice(0, 4).map((product, idx) => (
                  <TopProductCard
                    key={product._id}
                    product={product}
                    rank={idx}
                    maxRevenue={maxProductRevenue}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
